import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const LOGO = 'https://alldoneroofing1.com/wp-content/uploads/2025/05/WhatsApp_Image_2025-06-09_at_22.16.18_f08d1b10-removebg-preview-1-e1749507470340.png';

const SERVICES_NAV = [
  { label: 'Roof Replacement', to: '/services/roof-replacement' },
  { label: 'Roof Repairs', to: '/services/roof-repairs' },
  { label: 'Storm Damage Restoration', to: '/services/storm-damage-restoration' },
  { label: 'Insurance Claim Assistance', to: '/services/insurance-claim-assistance' },
  { label: 'Commercial Roofing', to: '/services/commercial-roofing' },
];

const AREAS_NAV = [
  { label: 'Gulfport, MS', to: '/locations/gulfport-ms' },
  { label: 'Biloxi, MS', to: '/locations/biloxi-ms' },
  { label: 'Ocean Springs, MS', to: '/locations/ocean-springs-ms' },
  { label: 'Long Beach, MS', to: '/locations/long-beach-ms' },
  { label: 'Diamondhead, MS', to: '/locations/diamondhead-ms' },
  { label: 'Bay St. Louis, MS', to: '/locations/bay-st-louis-ms' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [areasOpen, setAreasOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAreasOpen, setMobileAreasOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setMobileServicesOpen(false);
    setMobileAreasOpen(false);
  }, [location]);

  const scrollToEstimate = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      document.getElementById('estimate')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-obsidian border-b border-white/8 transition-all duration-500">
      {/* Top contact bar */}
      {!scrolled && (
        <div className="border-b border-white/8">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-6 text-[11px] tracking-widest uppercase">
              <a href="tel:2285473887" className="flex items-center gap-2 text-white/50 hover:text-gold transition-colors cursor-pointer">
                <i className="ri-phone-fill text-gold text-xs"></i>
                <span>(228) 547-3887</span>
              </a>
              <a href="mailto:alldoneroofing1@gmail.com" className="hidden sm:flex items-center gap-2 text-white/50 hover:text-gold transition-colors cursor-pointer">
                <i className="ri-mail-fill text-gold text-xs"></i>
                <span>alldoneroofing1@gmail.com</span>
              </a>
            </div>
            <div className="flex items-center gap-3">
              <a href="https://www.facebook.com/alldoneroofing/" target="_blank" rel="nofollow noopener noreferrer"
                className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-gold transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-sm"></i>
              </a>
              <span className="text-[10px] tracking-widest uppercase text-white/25 hidden md:block">GAF Certified &amp; Fortification Certified</span>
            </div>
          </div>
        </div>
      )}

      {/* Main navigation */}
      <nav className="transition-all duration-500">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 cursor-pointer">
            <img src={LOGO} alt="All Done Roofing LLC" className="h-14 w-auto object-contain" />
          </Link>

          {/* Desktop navigation */}
          <ul className="hidden lg:flex items-center gap-8 text-[12px] font-semibold tracking-widest uppercase">
            <li>
              <Link to="/" className="text-white/70 hover:text-white transition-colors whitespace-nowrap cursor-pointer">Home</Link>
            </li>
            <li>
              <Link to="/about" className="text-white/70 hover:text-white transition-colors whitespace-nowrap cursor-pointer">About</Link>
            </li>

            {/* Services dropdown */}
            <li className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
              <button className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                Services <i className="ri-arrow-down-s-line text-[10px] text-gold"></i>
              </button>
              <div className={`absolute top-full left-0 mt-3 w-60 bg-obsidian border border-white/10 rounded-lg overflow-hidden transition-all duration-200 origin-top ${servicesOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}`}>
                <div className="py-1">
                  {SERVICES_NAV.map(s => (
                    <Link key={s.label} to={s.to} onClick={() => setServicesOpen(false)}
                      className="flex items-center gap-3 px-5 py-3 text-[11px] tracking-wider uppercase text-white/60 hover:text-gold hover:bg-white/5 transition-all cursor-pointer">
                      <span className="w-1 h-1 rounded-full bg-gold shrink-0"></span>
                      {s.label}
                    </Link>
                  ))}
                </div>
              </div>
            </li>

            {/* Areas dropdown */}
            <li className="relative" onMouseEnter={() => setAreasOpen(true)} onMouseLeave={() => setAreasOpen(false)}>
              <button className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                Locations <i className="ri-arrow-down-s-line text-[10px] text-gold"></i>
              </button>
              <div className={`absolute top-full left-0 mt-3 w-52 bg-obsidian border border-white/10 rounded-lg overflow-hidden transition-all duration-200 origin-top ${areasOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}`}>
                <div className="py-1">
                  {AREAS_NAV.map(a => (
                    <Link key={a.label} to={a.to} onClick={() => setAreasOpen(false)}
                      className="flex items-center gap-3 px-5 py-2.5 text-[11px] tracking-wider uppercase text-white/60 hover:text-gold hover:bg-white/5 transition-all cursor-pointer">
                      <i className="ri-map-pin-2-line text-gold text-xs shrink-0"></i>
                      {a.label}
                    </Link>
                  ))}
                </div>
              </div>
            </li>

            <li>
              <a href={location.pathname === '/' ? '#testimonials' : '/#testimonials'}
                onClick={e => { if (location.pathname === '/') { e.preventDefault(); document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth' }); } }}
                className="text-white/70 hover:text-white transition-colors whitespace-nowrap cursor-pointer">Reviews</a>
            </li>
          </ul>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:2285473887" className="flex items-center gap-2 text-white/60 hover:text-gold transition-colors text-[11px] font-semibold tracking-widest uppercase whitespace-nowrap cursor-pointer">
              <i className="ri-phone-fill text-gold"></i>
              <span>(228) 547-3887</span>
            </a>
            <a href={location.pathname === '/' ? '#estimate' : '/'}
              onClick={scrollToEstimate}
              className="bg-gold text-ink font-bold text-[11px] tracking-widest uppercase px-6 py-3 rounded hover:bg-gold-light transition-colors whitespace-nowrap cursor-pointer">
              Free Estimate
            </a>
          </div>

          {/* Mobile: call + hamburger */}
          <div className="lg:hidden flex items-center gap-3">
            <a href="tel:2285473887" className="w-9 h-9 flex items-center justify-center text-gold cursor-pointer">
              <i className="ri-phone-fill text-lg"></i>
            </a>
            <button onClick={() => setMenuOpen(!menuOpen)} className="w-9 h-9 flex items-center justify-center text-white cursor-pointer">
              <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div className={`lg:hidden transition-all duration-300 overflow-hidden ${menuOpen ? 'max-h-[700px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="bg-obsidian border-t border-white/8 px-4 py-6 flex flex-col gap-1">
          <Link to="/" className="text-white/70 hover:text-gold py-3 text-[12px] font-semibold tracking-widest uppercase border-b border-white/5 cursor-pointer">Home</Link>
          <Link to="/about" className="text-white/70 hover:text-gold py-3 text-[12px] font-semibold tracking-widest uppercase border-b border-white/5 cursor-pointer">About</Link>

          {/* Mobile services */}
          <div className="border-b border-white/5">
            <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              className="w-full flex items-center justify-between text-white/70 hover:text-gold py-3 text-[12px] font-semibold tracking-widest uppercase cursor-pointer">
              Services
              <i className={`ri-arrow-down-s-line text-gold transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileServicesOpen && (
              <div className="flex flex-col pl-4 pb-2 gap-1">
                {SERVICES_NAV.map(s => (
                  <Link key={s.label} to={s.to}
                    className="text-white/50 hover:text-gold py-2 text-[11px] font-medium tracking-wider uppercase cursor-pointer flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-gold/60 shrink-0"></span>
                    {s.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Mobile locations */}
          <div className="border-b border-white/5">
            <button onClick={() => setMobileAreasOpen(!mobileAreasOpen)}
              className="w-full flex items-center justify-between text-white/70 hover:text-gold py-3 text-[12px] font-semibold tracking-widest uppercase cursor-pointer">
              Locations
              <i className={`ri-arrow-down-s-line text-gold transition-transform ${mobileAreasOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileAreasOpen && (
              <div className="flex flex-col pl-4 pb-2 gap-1">
                {AREAS_NAV.map(a => (
                  <Link key={a.label} to={a.to}
                    className="text-white/50 hover:text-gold py-2 text-[11px] font-medium tracking-wider uppercase cursor-pointer flex items-center gap-2">
                    <i className="ri-map-pin-2-line text-gold text-xs shrink-0"></i>
                    {a.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="pt-5 flex flex-col gap-3">
            <a href="tel:2285473887" className="flex items-center justify-center gap-3 border border-white/15 text-white py-3.5 rounded text-[12px] font-semibold tracking-widest uppercase cursor-pointer hover:border-gold/40 transition-colors">
              <i className="ri-phone-fill text-gold"></i> (228) 547-3887
            </a>
            <Link to="/about"
              className="bg-gold text-ink font-bold py-3.5 rounded text-[12px] tracking-widest uppercase text-center cursor-pointer hover:bg-gold-light transition-colors">
              Request Free Estimate
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
